// src/components/mdx/client/visualizations/CreativeFatigueChart.client.tsx

/**
 * Creative Fatigue Chart
 * 
 * This component visualizes the concept of creative fatigue in advertising,
 * showing how ad performance (ROAS) typically declines over time as audiences
 * become less responsive to repeated creative exposure.
 * 
 * OPTIMIZATION NOTE:
 * This component uses centralized imports from '~/lib/charts' instead of direct imports
 * from 'recharts'. This approach provides several bundle optimization benefits:
 * 
 * 1. Shared module chunking - All chart components share the same recharts code chunks
 * 2. Enhanced tree-shaking - More efficient dead code elimination across the codebase
 * 3. Import cost amortization - The fixed overhead cost of importing recharts is shared
 * 4. Deduplication - Better handling of nested dependencies within recharts
 * 5. Consistent code splitting - Creates cleaner boundaries for code splitting
 * 
 * As our application contains multiple visualizations, this approach significantly
 * reduces the overall bundle size compared to direct imports from 'recharts'.
 */

"use client"

import { forwardRef, useMemo } from 'react'

import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ReferenceArea,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from '~/lib/charts/client'
import { getOrCreateTooltipWrapper } from '~/lib/charts/tooltip'
import type { CustomTooltipProps } from '~/lib/charts/tooltip'
import type { NameType, TooltipContentType, ValueType } from '~/lib/charts/types'
import {
  calculateCreativeFatigueMovingAverage,
  generateCreativeFatigueData,
  type CreativeFatigueDataPoint
} from '~/components/mdx/client/visualizations/creativeFatigueChartData'
import { useMediaQuery } from '~/lib/hooks/useMediaQuery'
import { createDeterministicRandom, hashSeed } from '~/lib/utils/random'
import { ChartVisibilityBoundary } from '~/components/mdx/client/visualizations/ChartVisibilityBoundary.client'

/**
 * Props for the ChartContainer component
 * @interface ChartContainerProps
 * @extends React.HTMLAttributes<HTMLDivElement>
 * @property {object} [config] - Configuration for chart series
 */
interface ChartContainerProps extends React.HTMLAttributes<HTMLDivElement> {
  config?: Record<string, {
    label: string
    color: string
  }>
}

/**
 * A container component for charts with forwarded refs
 */
const ChartContainer = forwardRef<HTMLDivElement, ChartContainerProps>(({ className, ...props }, ref) => {
  return (
    <div className={className} {...props} ref={ref}>
      {props.children}
    </div>
  )
})
ChartContainer.displayName = 'ChartContainer'

/**
 * Custom tooltip component for the chart - optimized for performance
 */
const CustomTooltip = ({ active, payload, label }: CustomTooltipProps): JSX.Element | null => {
  if (!active || !payload?.length) {
    return null
  }
  
  return (
    <div className="rounded-lg border border-gray-700 bg-gray-800/90 p-4 shadow-lg backdrop-blur-sm">
      <p className="mb-2 font-medium text-gray-300">Exposure: {label} days</p>
      <p className="text-sm text-gray-400">
        ROAS: <span className="font-medium text-teal-300">{payload[0]?.value.toFixed(2)}</span>
      </p>
      <p className="text-sm text-gray-400">
        Avg ROAS: <span className="font-medium text-purple-300">{payload[1]?.value.toFixed(2)}</span>
      </p>
    </div>
  )
}

interface CreativeFatigueChartContentProps {
  /** Whether the chart is visible. Used to pause expensive animations off-screen. */
  isActive: boolean
}

/**
 * Creative Fatigue Chart body.
 *
 * Extracted so we can defer mounting with ChartVisibilityBoundary without breaking
 * hook ordering and to ensure that we only allocate chart data once the section is visible.
 */
function CreativeFatigueChartContent({ isActive }: CreativeFatigueChartContentProps): JSX.Element {
  const numPoints = 100
  const chartData = useMemo<CreativeFatigueDataPoint[]>(() => {
    const rng = createDeterministicRandom(hashSeed('creative-fatigue-chart', numPoints))
    const rawData = generateCreativeFatigueData(numPoints, rng)
    return calculateCreativeFatigueMovingAverage(rawData, 10)
  }, [numPoints])

  const { stablePhaseEnd, falseDipPhaseEnd, recoveryPhaseEnd, fatiguePhaseEnd } = useMemo(() => {
    const stablePhase = 0.35
    const falseDipPhase = 0.1
    const recoveryPhase = 0.1

    return {
      stablePhaseEnd: Math.floor(numPoints * stablePhase),
      falseDipPhaseEnd: Math.floor(numPoints * (stablePhase + falseDipPhase)),
      recoveryPhaseEnd: Math.floor(numPoints * (stablePhase + falseDipPhase + recoveryPhase)),
      fatiguePhaseEnd: numPoints - 1
    }
  }, [numPoints])

  const isMobile = useMediaQuery('(max-width: 768px)')
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)')
  const tooltipContent = useMemo(() => getOrCreateTooltipWrapper(CustomTooltip), [])
  const margin = useMemo(
    () =>
      isMobile
        ? { top: 10, right: 10, left: 0, bottom: 10 }
        : { top: 20, right: 15, left: 10, bottom: 20 },
    [isMobile]
  )

  const colorPalette = useMemo(
    () => ({
      grid: "rgb(75, 85, 99)",
      text: "rgb(156, 163, 175)",
      roas: "rgb(94, 234, 212)",
      movingAvg: "rgb(216, 180, 254)"
    }),
    []
  )

  const animateSeries = isActive && !prefersReducedMotion

  return (
    <div className="overflow-hidden rounded-xl bg-gradient-to-br from-gray-900 to-gray-800 shadow-2xl sm:rounded-3xl">
      <div className="p-4 sm:p-8">
          <h2 className="mb-1 text-xl font-bold text-transparent sm:mb-2 sm:text-3xl bg-clip-text bg-gradient-to-r from-teal-300 to-purple-400">
            Ad Creative Performance Over Time
          </h2>
          <p className="mb-3 text-sm text-gray-400 sm:mb-6 sm:text-base">
            Visualizing ROAS trends and potential creative fatigue
          </p>
          <ChartContainer
            config={{
              roas: {
                label: "Daily ROAS",
                color: "rgb(94, 234, 212)", // teal-300
              },
              movingAverage: {
                label: "10-Day Moving Average ROAS",
                color: "rgb(216, 180, 254)", // purple-300
              },
            }}
            className="aspect-[4/3] w-full sm:aspect-[16/9]"
          >
            <ResponsiveContainer
              width="100%"
              height="100%"
              debounce={300}
              initialDimension={{ width: 800, height: 400 }}
            >
              <LineChart
                data={chartData}
                margin={margin}
                accessibilityLayer={false}
                throttleDelay={50}
                syncId="creativeFatigueChart"
              >
                <CartesianGrid opacity={0.2} stroke={colorPalette.grid} strokeDasharray="3 3" />
                <XAxis
                  dataKey="exposure"
                  stroke={colorPalette.text}
                  fontSize={isMobile ? 10 : 12}
                  tickLine={false}
                  axisLine={false}
                  interval={isMobile ? 19 : 9}
                  label={
                    isMobile
                      ? undefined
                      : {
                          value: "Exposure (Days)",
                          position: "insideBottom",
                          offset: -10,
                          fill: colorPalette.text
                        }
                  }
                />
                <YAxis
                  stroke={colorPalette.text}
                  fontSize={isMobile ? 10 : 12}
                  tickLine={false}
                  axisLine={false}
                  domain={[0, 7]}
                  width={isMobile ? 25 : 40}
                  label={
                    isMobile
                      ? undefined
                      : {
                          value: "ROAS",
                          angle: -90,
                          position: "insideLeft",
                          offset: 10,
                          fill: colorPalette.text
                        }
                  }
                />
                <Tooltip
                  content={tooltipContent as TooltipContentType<ValueType, NameType>}
                  animationDuration={100}
                  animationEasing="ease-out"
                  shared={true}
                  allowEscapeViewBox={{ x: false, y: false }}
                  filterNull={false}
                  includeHidden={false}
                />
                <Legend
                  verticalAlign="top"
                  height={isMobile ? 24 : 36}
                  iconType="circle"
                  iconSize={isMobile ? 6 : 8}
                  wrapperStyle={{ 
                    fontSize: isMobile ? "10px" : "12px", 
                    color: colorPalette.text,
                    paddingTop: isMobile ? "5px" : "0px"
                  }}
                />
                <Line
                  dataKey="roas"
                  type="monotone"
                  strokeWidth={isMobile ? 0.8 : 1}
                  dot={false}
                  name="Daily ROAS"
                  animationDuration={animateSeries ? 800 : 0}
                  animationEasing="ease-out"
                  connectNulls={false}
                  stroke={colorPalette.roas}
                />
                <Line
                  dataKey="movingAverage"
                  type="monotone"
                  strokeWidth={isMobile ? 2 : 3}
                  dot={false}
                  name="10-Day Moving Average ROAS"
                  animationDuration={animateSeries ? 800 : 0}
                  animationEasing="ease-out"
                  connectNulls={false}
                  stroke={colorPalette.movingAvg}
                />
                {/* Reference areas for different phases */}
                <ReferenceArea
                  x1={0}
                  x2={stablePhaseEnd}
                  y1={0}
                  y2={7}
                  strokeOpacity={0}
                  fill="rgba(0, 255, 0, 0.1)"
                />
                <ReferenceArea
                  x1={stablePhaseEnd}
                  x2={falseDipPhaseEnd}
                  y1={0}
                  y2={7}
                  strokeOpacity={0}
                  fill="rgba(255, 165, 0, 0.15)"
                />
                <ReferenceArea
                  x1={falseDipPhaseEnd}
                  x2={recoveryPhaseEnd}
                  y1={0}
                  y2={7}
                  strokeOpacity={0}
                  fill="rgba(0, 191, 255, 0.15)"
                />
                <ReferenceArea
                  x1={recoveryPhaseEnd}
                  x2={fatiguePhaseEnd}
                  y1={0}
                  y2={7}
                  strokeOpacity={0}
                  fill="rgba(255, 0, 0, 0.1)"
                />
              </LineChart>
            </ResponsiveContainer>
          </ChartContainer>
          <div className="mt-3 flex flex-wrap justify-center gap-2 text-xs sm:mt-6 sm:gap-4 sm:text-sm">
            <div className="flex items-center">
              <div className="mr-1 h-3 w-3 rounded bg-green-500 opacity-20 sm:mr-2 sm:h-4 sm:w-4" />
              <span className="text-gray-400">Stable Performance</span>
            </div>
            <div className="flex items-center">
              <div className="mr-1 h-3 w-3 rounded bg-orange-500 opacity-20 sm:mr-2 sm:h-4 sm:w-4" />
              <span className="text-gray-400">False Dip</span>
            </div>
            <div className="flex items-center">
              <div className="mr-1 h-3 w-3 rounded bg-blue-400 opacity-20 sm:mr-2 sm:h-4 sm:w-4" />
              <span className="text-gray-400">Recovery Phase</span>
            </div>
            <div className="flex items-center">
              <div className="mr-1 h-3 w-3 rounded bg-red-500 opacity-20 sm:mr-2 sm:h-4 sm:w-4" />
              <span className="text-gray-400">Creative Fatigue</span>
            </div>
          </div>
      </div>
    </div>
  )
}

/**
 * Creative Fatigue Chart Component
 *
 * Wrapped in ChartVisibilityBoundary so data generation and the Recharts runtime
 * are deferred until the visualization is about to enter the viewport. This keeps
 * long-form blog posts responsive by avoiding heavy work during the initial paint.
 */
export default function CreativeFatigueChart(): JSX.Element {
  return (
    <div className="mx-auto w-full max-w-4xl p-2 sm:p-6">
      <ChartVisibilityBoundary aria-label="Ad creative fatigue visualization">
        {({ hasEntered, isIntersecting }) =>
          hasEntered ? <CreativeFatigueChartContent isActive={isIntersecting} /> : null
        }
      </ChartVisibilityBoundary>
    </div>
  )
}
